import { GridSortModel } from '@mui/x-data-grid-pro'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React, { useMemo, useState } from 'react'
import { buildCardFeedsColumns } from 'src/app/CardFeeds/components/cardFeedsColumns'
import * as style from 'src/app/CardFeeds/components/CardFeedsTable.style'
import CardFeedStatusFilter from 'src/app/CardFeeds/components/CardFeedStatusFilter'
import {
  CardFeedSortField,
  CardFeedsView,
  selectVisibleFeeds,
} from 'src/app/CardFeeds/utils/selectVisibleFeeds'
import Box from 'src/components/Box/Box'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import { useTranslate } from 'src/localization/Trns'
import {
  CardFeedResponse,
  CardFeedStatus,
} from 'src/queries/cardFeeds/cardFeeds.models'

export interface CardFeedsTableProps {
  feeds: CardFeedResponse[]
  isLoading: boolean
  isError: boolean
  onSeeCards: (feedId: string) => void
}

const GRID_HEIGHT = 560
// Non-row chrome the grid height has to cover so the last row isn't clipped:
// the shared DataGrid wrapper renders at `height - 60` (see DataGrid.tsx), and
// inside it the grid reserves a 44px column header and a 52px footer ("Total
// Rows"). Sizing to rows alone (the old +104 omitted the footer) cut off one
// row.
const GRID_CHROME_HEIGHT = 60 + 44 + 52
const SORTABLE_FIELDS: CardFeedSortField[] = [
  'cardProgramName',
  'referenceId',
  'cardNetwork',
  'dateConnected',
  'cardCount',
  'status',
]

const CardFeedsTable: React.FC<CardFeedsTableProps> = ({
  feeds,
  isLoading,
  isError,
  onSeeCards,
}) => {
  const translate = useTranslate()
  const [searchValue, setSearchValue] = useState('')
  const [statuses, setStatuses] = useState<CardFeedStatus[]>([])
  const [sortModel, setSortModel] = useState<GridSortModel>([])

  const view: CardFeedsView = useMemo(() => {
    const sort = sortModel[0]
    const sortField =
      sort && SORTABLE_FIELDS.includes(sort.field as CardFeedSortField)
        ? (sort.field as CardFeedSortField)
        : undefined
    return {
      search: searchValue,
      statuses,
      sortField,
      sortDirection: sortField
        ? sort?.sort === 'desc'
          ? 'DESC'
          : 'ASC'
        : undefined,
    }
  }, [searchValue, statuses, sortModel])

  const visibleFeeds = useMemo(
    () => selectVisibleFeeds(feeds, view),
    [feeds, view],
  )

  const columns = useMemo(
    () => buildCardFeedsColumns({ translate, onSeeCards }),
    [translate, onSeeCards],
  )

  const isFiltering = searchValue.trim().length > 0 || statuses.length > 0

  const noRowsOverlay = (): React.ReactElement => (
    <div
      css={style.overlay}
      data-testid={isFiltering ? 'card-feeds-no-match' : 'card-feeds-empty'}
    >
      {isFiltering
        ? translate('No card feeds match your search or filters.')
        : translate('No card feeds yet.')}
    </div>
  )

  return (
    <Box
      noPadding
      outlineBorder
      css={style.tableCard}
      data-testid="card-feeds-table"
    >
      <div css={style.toolbar}>
        <div css={style.search}>
          <TextInput
            value={searchValue}
            onChange={(e): void => setSearchValue(e.target.value)}
            placeholder={translate('Search...')}
            aria-label={translate('Search card feeds')}
            testId="card-feeds-search"
          />
        </div>
        <CardFeedStatusFilter statuses={statuses} onChange={setStatuses} />
      </div>

      {isError ? (
        <div css={style.overlay}>
          <span role="alert" data-testid="card-feeds-error">
            {translate("We couldn't load your card feeds. Please try again.")}
          </span>
        </div>
      ) : isLoading ? (
        <div
          css={style.overlay}
          role="status"
          aria-live="polite"
          data-testid="card-feeds-loading"
        >
          {translate('Loading...')}
        </div>
      ) : (
        <div css={style.gridWrap}>
          <DataGrid<CardFeedsView, CardFeedResponse>
            height={
              visibleFeeds.length > 0
                ? Math.min(
                    visibleFeeds.length * DataGridRowHeight.Default +
                      GRID_CHROME_HEIGHT,
                    GRID_HEIGHT,
                  )
                : GRID_HEIGHT
            }
            rows={visibleFeeds}
            columns={columns}
            filtersBody={view}
            rowHeight={DataGridRowHeight.Default}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            // The whole row opens the feed's details (the same destination as
            // the See Cards action). This is a pointer affordance; keyboard/AT
            // users still navigate via the focusable See Cards button, which
            // stops propagation so it isn't double-counted.
            onRowClick={(params): void => onSeeCards(params.row.id)}
            disableColumnFilter
            disableRowSelectionOnClick
            slots={{ noRowsOverlay }}
          />
        </div>
      )}
    </Box>
  )
}

export default CardFeedsTable
